/** @jsx jsx */
import React, { useEffect } from 'react';
import { css, jsx } from '@compiled/react';
import { graphql, usePreloadedQuery, type PreloadedQuery } from 'react-relay';
import StandardButton from '@atlaskit/button/standard-button';
import IssuesIcon from '@atlaskit/icon/core/migration/issues';
import { ModalBody, ModalTitle, ModalFooter } from '@atlaskit/modal-dialog';
import { Stack, Inline, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { token } from '@atlaskit/tokens';
import { useFlagsService } from '@atlassian/jira-flags';
import { useIntl } from '@atlassian/jira-intl';
import { JiraModal as Modal } from '@atlassian/jira-modal/src/ui/jira-modal.tsx';
import type { issuesModal_designIssueQuery } from '@atlassian/jira-relay/src/__generated__/issuesModal_designIssueQuery.graphql';
import { IssuesModalBody } from './body/index.tsx';
import messages from './messages.tsx';

interface Props {
	queryRef: PreloadedQuery<issuesModal_designIssueQuery>;
	onClose: () => void;
}

export const IssueModalLoading = () => (
	<Modal
		messageId="version-issue-associated-designs.ui.design-carousel.design-card.issues-modal.modal"
		messageType="transactional"
	>
		<ModalBody>
			<Stack xcss={modalBodyLoadingStyles}>
				<Spinner size="large" />
			</Stack>
		</ModalBody>
	</Modal>
);

export const IssuesModal = ({ queryRef, onClose }: Props) => {
	const { formatMessage } = useIntl();
	const { showFlag } = useFlagsService();

	const data = usePreloadedQuery(
		graphql`
			query issuesModal_designIssueQuery($designAris: [ID!]!, $versionId: ID!, $cloudAri: ID!) {
				devOps @required(action: THROW) {
					designEntityDetails(ids: $designAris)
						@required(action: THROW)
						@optIn(to: "DevOpsDesignsInJiraProjects") {
						url @required(action: THROW)
						displayName @required(action: THROW)
						provider(id: $cloudAri) @required(action: THROW) {
							logoUrl @required(action: THROW)
						}
						...body_versionIssueAssociatedDesigns_IssuesModalBody
					}
				}
			}
		`,
		queryRef,
	);

	const designEntity = data.devOps.designEntityDetails[0];
	// This shouldn't happen much in real world, but can happen when the target design is deleted while the user is looking at the page.
	useEffect(() => {
		if (designEntity == null) {
			showFlag({
				messageId:
					'version-issue-associated-designs.ui.design-carousel.design-card.issues-modal.show-flag.error',
				messageType: 'transactional',
				type: 'error',
				title: formatMessage(messages.errorFlagTitle),
				description: formatMessage(messages.errorFlagDescription),
			});
		}
	}, [designEntity, formatMessage, showFlag]);

	useEffect(() => {
		// Why? suggested here: https://hello.atlassian.net/wiki/spaces/A11YKB/pages/3021481389/Modal+Dialog+Focus#General-Focus
		setTimeout(() => {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			const modalElement = document.querySelector<HTMLElement>("[role='dialog']");
			modalElement?.focus();
		}, 50);
	}, []);

	if (designEntity == null) {
		return null;
	}

	return (
		<Modal
			width="small"
			onClose={() => onClose()}
			autoFocus={false}
			messageId="version-issue-associated-designs.ui.design-carousel.design-card.issues-modal.modal.1"
			messageType="transactional"
		>
			<ModalTitle>
				<Stack space="space.150" xcss={modalTitleStyles}>
					<Inline>
						<img src={designEntity?.provider.logoUrl} css={modalIconStyles} alt="" />
						<a css={titleStyles} href={designEntity?.url} target="_blank">
							{designEntity?.displayName}
						</a>
					</Inline>
					<Stack space="space.075">
						{/* eslint-disable-next-line @atlaskit/design-system/use-primitives-text */}
						<p css={linkedToStyles}>{formatMessage(messages.linkedTo)}</p>
						<Inline alignBlock="center" space="space.050" xcss={multipleIssuesStyles}>
							<IssuesIcon color={token('color.icon.accent.blue')} LEGACY_size="small" label="" />
							{formatMessage(messages.manyIssues)}
						</Inline>
					</Stack>
				</Stack>
			</ModalTitle>
			<ModalBody>
				<IssuesModalBody devOpsDesign={designEntity} />
			</ModalBody>
			<ModalFooter>
				<StandardButton appearance="primary" onClick={() => onClose()}>
					{formatMessage(messages.modalCloseButtonLabel)}
				</StandardButton>
			</ModalFooter>
		</Modal>
	);
};

const modalIconStyles = css({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
	height: '24px',
	minWidth: '16px',
	marginRight: token('space.100'),
});

const titleStyles = css({
	textDecoration: 'none',
	color: 'inherit',
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-unsafe-selectors -- Ignored via go/DSP-18766
	':hover': {
		textDecoration: 'underline',
		color: 'inherit',
	},
	fontWeight: token('font.weight.semibold'),
});

const modalTitleStyles = xcss({
	paddingTop: 'space.300',
	paddingBottom: 'space.200',
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
});

const modalBodyLoadingStyles = xcss({
	justifyContent: 'center',
	alignItems: 'center',
	minHeight: '300px',
	height: '300px',
});

const linkedToStyles = css({
	margin: 0,
	font: token('font.body.small'),
	fontWeight: token('font.weight.regular'),

	color: token('color.text.subtlest'),
});

const multipleIssuesStyles = xcss({
	font: 'font.body',
});
